import React, { useEffect, useRef, useState } from "react";
import { IGame } from "../../../core/engine/types/game";
import GameView from "../../../core/engine/views/game-view";

/**
 * Calculate the height of a GameView for a given width.
 * This matches the calculation logic in GameView.init()
 */
export function calculateGameViewHeight(width: number): number {
  // Padding is 5% of width
  const padding = width * 0.05;
  
  // Board width is 10/11 of total width (Bonus takes 1/11)
  const boardWidth = (width / 11) * 10;
  
  // Block size calculation matches CanvasView: (width - 2) / 10
  const blockSize = (boardWidth - 2) / 10;
  
  // Queue height matches BlockQueueView: blockSize * 3.5
  const queueHeight = blockSize * 3.5;
  
  // Canvas view height matches CanvasView: 20 * blockSize + 2
  const canvasViewHeight = 20 * blockSize + 2;
  
  // Total height: top padding + queue + middle padding + board + bottom padding
  const height = padding + queueHeight + (padding / 2) + canvasViewHeight + padding;
  
  return height;
}

interface PlayerBoardViewProps {
  game?: IGame; // Game instance for rendering with GameView (includes board, queue, and garbage sidebar)
  playerName: string;
  width?: number;
}

const PlayerBoardView: React.FC<PlayerBoardViewProps> = ({
  game,
  playerName,
  width = 200,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameViewRef = useRef<GameView | null>(null);
  const [displayHeight, setDisplayHeight] = useState<number>(width * 1.3);
  
  // Create GameView instance for rendering (includes board, queue, and garbage sidebar)
  useEffect(() => {
    if (!game) {
      gameViewRef.current = null;
      setDisplayHeight(width * 1.3);
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Create GameView with the canvas element and game
    // GameView includes CanvasView, BlockQueueView, and BonusView
    // Use a minimum render width to ensure quality, but scale to fit the provided width
    const renderWidth = Math.max(width, 400);
    gameViewRef.current = new GameView(canvas, game, renderWidth);
    
    // Calculate display height based on actual GameView height
    // Since canvas is rendered at renderWidth but displayed at width, scale height proportionally
    const actualHeight = gameViewRef.current.height;
    const scaledHeight = (actualHeight / renderWidth) * width;
    setDisplayHeight(scaledHeight);

    return () => {
      gameViewRef.current = null;
    };
  }, [game, width, playerName]);

  // Handle resize when width changes
  useEffect(() => {
    if (!gameViewRef.current || !width || !canvasRef.current) return;
    const renderWidth = Math.max(width, 400);
    gameViewRef.current.resize(renderWidth);
    
    // Update display height after resize
    const actualHeight = gameViewRef.current.height;
    const scaledHeight = (actualHeight / renderWidth) * width;
    setDisplayHeight(scaledHeight);
  }, [width]);

  // Render loop for game-based rendering
  useEffect(() => {
    if (!game || !gameViewRef.current) return;
    
    let rafId: number;
    
    const renderLoop = () => {
      if (gameViewRef.current) {
        gameViewRef.current.draw(performance.now());
      }
      rafId = requestAnimationFrame(renderLoop);
    };
    
    renderLoop();
    
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [game]);

  if (!game) {
    // GameView aspect ratio is approximately Height = 1.3 * Width (includes queue at top)
    const loadingHeight = width * 1.3;
    return (
      <div className="player-board" style={{ 
        width: `${width}px`,
        height: `${loadingHeight}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.2)",
        border: "1px solid #666"
      }}>
        <div style={{ color: "#999", fontSize: "12px" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="player-board" style={{
      position: "relative",
      padding: "2px" // Small padding to prevent border clipping if needed
    }}>
      <canvas
        ref={canvasRef}
        style={{
          width: `${width}px`,
          height: `${displayHeight}px`,
          maxWidth: "100%",
          maxHeight: "100%",
          display: "block",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      />
      <style>{`
        .player-board {
          display: block;
          width: 100%;
          max-width: 100%;
          box-sizing: "border-box";
        }
      `}</style>
    </div>
  );
};

export default React.memo(PlayerBoardView);
